import { useEffect, useState } from "react";

import i18next from "i18next";
import moment from "moment";
import { MdOutlineAccountBalanceWallet } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import { setPaymentMethod } from "../../../store/orderSlice";

import { DotsLoadingComponent } from "../../ui";

import { useAuth } from "../../../context/auth";
import { useToggle } from "../../../hooks";
import { parserLangCode } from "../../../utils";
import RadioButtonComponent from "../../ui/RadioButton";
import PaymentCommissionModalComponent from "../../ui/PaymentCommissionModal";
import { markCommissionNoticeShown, wasCommissionNoticeShown } from "../../../utils/paymentCommissionNotice";
import OrderTimelimitComponent from "../OrderTimelimitComponent";
import { TimelimitExpired } from "../OrderTimelimitComponent/style";
import {
  BlockTitle,
  PaymentButton,
  PaymentButtonLoadingCover,
  PaymentMethodCheckboxBlock,
  PaymentMethodDescription,
  PaymentMethodItem,
  PaymentMethodLogo,
  PaymentMethodLogoContainer,
  PaymentMethodsBlock,
  TimelimitAndPaymentButtonBlock,
  Wrapper,
} from "./style";

const OrderPaymentFormComponent = ({ sendPaymentRequest, onProcessingChange }) => {
  const { language, t } = i18next;
  const {
    availablePaymentMethods,
    orderData: { order_id, timelimit, total_price, total_price_with_options },
    paymentMethod,
  } = useSelector(state => state.order);
  const dispatch = useDispatch();
  const [commissionNoticeMethodId, setCommissionNoticeMethodId] = useState(null);

  // выбран способ с комиссией (кликом или по умолчанию при открытии заказа) — один раз на заказ объясняем,
  // почему сумма к оплате выросла (флаг — utils/paymentCommissionNotice)
  useEffect(() => {
    const method = availablePaymentMethods?.find(item => item.id === paymentMethod);
    if (method?.code !== "BALANCE" && Number(method?.commission_value) > 0 && !wasCommissionNoticeShown(order_id)) {
      markCommissionNoticeShown(order_id);
      setCommissionNoticeMethodId(method.id);
    }
  }, [paymentMethod, availablePaymentMethods, order_id]);
  const {
    isTrue,
    set: { on, off },
  } = useToggle();
  const { isAuth, user } = useAuth();

  const [timerIsActive, setTimerIsActive] = useState(timelimit > 0);

  const handleOnClickItem = (id, code) => {
    if (id !== paymentMethod) {
      if (code !== "BALANCE") {
        dispatch(setPaymentMethod({ method: id }));
      }

      if (code === "BALANCE" && checkEnoughBalance()) {
        dispatch(setPaymentMethod({ method: id }));
      }
    }
  };

  const handleTimeEnd = () => setTimerIsActive(false);

  const handleSendPaymentRequest = () => {
    if (!isTrue) {
      on();
      if (onProcessingChange) onProcessingChange(true);
      sendPaymentRequest(() => {
        off();
        if (onProcessingChange) onProcessingChange(false);
      });
    }
  };

  const checkEnoughBalance = () => {
    return parseFloat(user?.balance || 0) + parseFloat(user?.credit_limit || 0) >= parseFloat(total_price_with_options ?? total_price);
  };

  if (timerIsActive) {
    const timelimitTime = moment()
      .locale(parserLangCode(language))
      .add(timelimit, "seconds")
      .format("DD MMM YYYY, dd, HH:mm");

    return (
      <>
        <Wrapper>
          <PaymentCommissionModalComponent
            methodId={commissionNoticeMethodId}
            onClose={() => setCommissionNoticeMethodId(null)}
          />

          <BlockTitle>{t("payment_method")}</BlockTitle>

          <PaymentMethodsBlock>
            {availablePaymentMethods?.map(method => {
              if (method.id === 0 && !isAuth) return null;

              return (
                <PaymentMethodItem
                  key={method.id}
                  $isActive={method.id === paymentMethod}
                  onClick={() => handleOnClickItem(method.id, method.code)}
                >
                  <PaymentMethodCheckboxBlock>
                    {/* <CheckboxComponent value={method.id === paymentMethod} hasLabel={false} /> */}
                    <RadioButtonComponent value={method.id === paymentMethod} hasLabel={false} />
                  </PaymentMethodCheckboxBlock>

                  {method.id === 0 ? (
                    <span className="balance-icon">
                      <MdOutlineAccountBalanceWallet />
                    </span>
                  ) : (
                    <PaymentMethodLogoContainer>
                      <PaymentMethodLogo src={method.logo} alt="payment-method-logo" />
                    </PaymentMethodLogoContainer>
                  )}

                  {method.id === 0
                    ? t("payment_with_balance")
                    : method.description[language] && (
                        <PaymentMethodDescription $isActive={method.id === paymentMethod}>
                          {method.code === "VSL" ? t("payment_with_vsl") : method.description[language]}
                        </PaymentMethodDescription>
                      )}

                  {method.id === 0 && !checkEnoughBalance() ? (
                    <span className="balance-not-enough">{t("not_enough_balance")}</span>
                  ) : null}
                </PaymentMethodItem>
              );
            })}
          </PaymentMethodsBlock>
        </Wrapper>

        <PaymentButton id="order-pay-button" onClick={handleSendPaymentRequest} disabled={isTrue}>
          {t("pay_for_the_order")}
          {isTrue && (
            <PaymentButtonLoadingCover>
              <DotsLoadingComponent color="#fff" />
            </PaymentButtonLoadingCover>
          )}
        </PaymentButton>

        <Wrapper>
          <TimelimitAndPaymentButtonBlock>
            <div>{t("left_before_the_time_limit")}:</div>
            <OrderTimelimitComponent timelimit={timelimit} onTimerEnd={handleTimeEnd} />
            <div>
              {t("expires")} {timelimitTime}
            </div>
          </TimelimitAndPaymentButtonBlock>
        </Wrapper>
      </>
    );
  }

  return (
    <Wrapper>
      <TimelimitExpired>{t("payment_timed_out")}</TimelimitExpired>
    </Wrapper>
  );
};

export default OrderPaymentFormComponent;
